import type React from "react";
import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FaSearch,
  FaBars,
  FaTimes,
  FaUser,
  FaSignOutAlt,
} from "react-icons/fa";
import { MdShoppingCart } from "react-icons/md";
import { BsHeartFill } from "react-icons/bs";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "@/redux/store";
import Logo from "../../assets/image/MBLogo.png";
import { searchProducts } from "@/utils/productApi";
import { useTranslation } from "react-i18next";
import i18next from "@/utils/i18n";
import { setSettings } from "@/redux/slices/settingsSlice";
import { Country, type ICountry } from "country-state-city";
import Dropdown from "./Dropdrop";
import {
  convertPrice,
  getCurrencyByCountry,
  getLanguageByCountry,
} from "@/utils/currencyCoverter";
import { motion, AnimatePresence } from "framer-motion";

const Header: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [word, setWord] = useState("");
  const [items, setItems] = useState<
    { _id: string; name: string; price: number }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const searchRef = useRef<HTMLDivElement>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  const user = useSelector((state: RootState) => state.user?.user || null);
  const vendor = useSelector((state: RootState) => state.vendor || null);
  const settings = useSelector((state: RootState) => state.settings);

  const countries = Country.getAllCountries();
  const filteredCountries = useMemo(
    () => countries.filter((country) =>
      country.name.toLowerCase().includes(countrySearch.toLowerCase())
    ),
    [countrySearch]
  );

  const handleCountryChange = (country: ICountry) => {
    const language = getLanguageByCountry(country.isoCode);
    const currency = getCurrencyByCountry(country.isoCode);
    i18next.changeLanguage(language, (err) => {
      if (err && process.env.NODE_ENV === "development") {
        console.error("Language change failed:", err);
      }
    });
    dispatch(setSettings({ language, currency, countryCode: country.isoCode }));
    setIsCountryOpen(false);
    setCountrySearch("");
  };

  const firstLetter = vendor?.vendor?.storeName
    ? vendor.vendor.storeName.charAt(0).toUpperCase()
    : vendor?.vendor?._id
      ? "V"
      : user?.name
        ? user.name.charAt(0).toUpperCase()
        : "";

  const dashboardLink = vendor?.vendor?._id ? "/app" : "/dashboard";
  const vendorLink = vendor?.vendor?._id ? "/app" : "/signup-vendor";
  const vendorText = vendor?.vendor?._id ? "Vendor Dashboard" : "Become a Vendor";

  const cartItems = useSelector((state: RootState) => state.cart.items);
  const totalQuantity = cartItems.reduce((total, item) => total + item.quantity, 0);

  const wishlistItems = useSelector((state: RootState) => state.wishlist.items);
  const totalWishlistQuantity = wishlistItems.reduce((total, item) => total + item.quantity, 0);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setItems([]);
      }
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setIsCountryOpen(false);
        setCountrySearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (word) {
        setIsLoading(true);
        searchProducts(word)
          .then((result) => setItems(result.products || []))
          .catch((err) => console.error("Search failed:", err))
          .finally(() => setIsLoading(false));
      } else {
        setItems([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [word]);

  return (
    // Removed bg-white shadow-md — nav is black so no white edge flash
    <header className="sticky top-0 z-50 w-full">

      {/* ── Top bar ── */}
      <div className="bg-[#ff710b] py-2 flex items-center justify-between px-4 md:px-10 text-white text-[10px] md:text-lg">
        <p className="font-medium">
          {t("welcome")}{" "}
          {vendor.vendor?.storeName
            ? `, ${vendor.vendor.storeName}`
            : vendor?.vendor?._id
              ? ", Vendor"
              : user?.name
                ? `, ${user.name}`
                : t("global_marketplaces")}
          !
        </p>
        <div className="flex items-center gap-6">
          <Link
            to="/random-product"
            className="hidden font-medium transition-colors duration-200 md:block hover:underline"
          >
            {t("shop_now")}
          </Link>
          <div className="relative" ref={countryDropdownRef}>
            <button
              onClick={() => setIsCountryOpen(!isCountryOpen)}
              className="flex items-center gap-2 font-medium transition-colors duration-200 hover:underline"
              aria-label={`Select country, current: ${settings.countryCode}`}
            >
              {countries.find((c) => c.isoCode === settings.countryCode)?.name || "Select Country"}
              <svg
                className={`w-4 h-4 transform ${isCountryOpen ? "rotate-180" : ""}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isCountryOpen && (
              <div className="absolute right-0 z-10 w-64 mt-2 overflow-y-auto text-black bg-white rounded-md shadow-lg top-full max-h-96">
                <div className="p-2">
                  <input
                    type="text"
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                    placeholder={t("select_country")}
                    className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-300"
                    autoFocus
                  />
                </div>
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <button
                      key={country.isoCode}
                      onClick={() => handleCountryChange(country)}
                      className="block w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                    >
                      {country.name} ({getCurrencyByCountry(country.isoCode)})
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-sm text-gray-500">No countries found</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Main nav ── */}
      <div className="bg-black">
        {/* Desktop View */}
        <div className="hidden md:flex items-center justify-between p-4 px-10">
          {/* Left: logo + nav links */}
          <div className="flex items-center gap-10">
            <Link to="/" className="flex items-center">
              <img src={Logo || "/placeholder.svg"} alt="MBLogo" className="w-16 md:w-20 brightness-0 invert" />
            </Link>

            <nav className="items-center hidden space-x-4 lg:flex lg:space-x-8">
              <Link
                to="/recently-viewed"
                className="text-sm font-medium text-white transition-colors duration-200 hover:text-orange-500"
              >
                Recently Viewed
              </Link>
              <Link
                to={vendorLink}
                className="text-sm font-medium text-white transition-colors duration-200 hover:text-orange-500"
                aria-label={vendorText}
              >
                {vendorText}
              </Link>
              {vendor?.vendor?._id && (
                <Link
                  to="/app/all-post"
                  className="text-sm font-medium text-white transition-colors duration-200 hover:text-orange-500"
                >
                  Timeline
                </Link>
              )}
            </nav>
          </div>

          {/* Right: search + icons + CTA */}
          <div className="flex items-center gap-4">
            <div className="relative w-56 md:w-64 lg:w-72" ref={searchRef}>
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
              <input
                type="text"
                value={word}
                onChange={(e) => setWord(e.target.value)}
                placeholder="Search products..."
                className="w-full bg-[#1e1e1e] border border-[#58585E] text-white placeholder-gray-500 rounded-[6px] py-2 pl-9 pr-10 text-sm focus:outline-none focus:border-[#ff710b] focus:ring-1 focus:ring-[#ff710b] transition-all duration-200"
              />
              {isLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {items.length > 0 && (
                <ul className="absolute z-50 top-full mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto max-h-60 text-black">
                  {items.map((item) => (
                    <li key={item._id} className="border-b border-gray-100 last:border-b-0">
                      <Link
                        to={`/product/${item._id}`}
                        onClick={() => setWord("")}
                        className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors duration-150"
                      >
                        <span className="font-medium">{item.name}</span>
                        <span className="font-semibold text-orange-600">
                          {convertPrice(item.price, "USD", settings.currency).toLocaleString(
                            settings.language, { style: "currency", currency: settings.currency }
                          )}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex items-center gap-1">
              <Link
                to="/dashboard/wishlist"
                className="relative p-2 transition-colors duration-200 rounded-full"
                aria-label="Wishlist"
              >
                <BsHeartFill size={20} className="text-white" />
                {totalWishlistQuantity > 0 && (
                  <span className="absolute -top-1 -right-1 text-xs text-white bg-[#ff710b] rounded-full h-5 w-5 flex items-center justify-center font-medium shadow-md">
                    {totalWishlistQuantity}
                  </span>
                )}
              </Link>

              <Link
                to="/cart"
                className="relative p-2 transition-colors duration-200 rounded-full"
                aria-label="Shopping Cart"
              >
                <MdShoppingCart size={20} className="text-white" />
                {totalQuantity > 0 && (
                  <span className="absolute -top-1 -right-1 text-xs text-white bg-[#ff710b] rounded-full h-5 w-5 flex items-center justify-center font-medium shadow-md">
                    {totalQuantity}
                  </span>
                )}
              </Link>

              <div className="ml-2">
                {vendor?.vendor?.avatar || vendor?.vendor?.businessLogo ? (
                  <Link to="/app" aria-label="Vendor Dashboard">
                    <div className="w-10 h-10 overflow-hidden border-2 border-orange-400 rounded-full">
                      <img
                        src={vendor.vendor.avatar || vendor.vendor.businessLogo || "/placeholder.svg"}
                        alt={`${vendor.vendor.storeName} logo`}
                        className="object-cover w-full h-full"
                      />
                    </div>
                  </Link>
                ) : firstLetter ? (
                  <Link to={dashboardLink} aria-label={vendor ? "Vendor Dashboard" : "User Dashboard"}>
                    <div className="flex items-center justify-center w-8 h-8 text-lg font-bold text-white bg-orange-500 rounded-full shadow-md ring-2 ring-orange-400">
                      {firstLetter}
                    </div>
                  </Link>
                ) : (
                  <Link
                    to="/selectpath"
                    className="px-5 py-2 text-sm text-white transition-colors duration-300 border border-orange-600 rounded-md hover:bg-orange-600"
                  >
                    Get started
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile View */}
        <div className="md:hidden">
          {/* Mobile Row 1: Logo and Quick Links */}
          <div className="flex items-center justify-between p-4 border-b border-[#222]">
            <Link to="/" className="flex items-center">
              <img src={Logo || "/placeholder.svg"} alt="MBLogo" className="w-12 brightness-0 invert" />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/recently-viewed" className="text-xs font-medium text-white hover:text-orange-500">
                Recently Viewed
              </Link>
              <Link to={vendorLink} className="text-xs font-medium text-white hover:text-orange-500">
                Become a Vendor
              </Link>
            </div>
          </div>

          {/* Mobile Row 2: Search, CTA, and Menu */}
          <div className="flex items-center gap-3 p-3 px-4">
            <div className="relative flex-1" ref={searchRef}>
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
              <input
                type="text"
                value={word}
                onChange={(e) => setWord(e.target.value)}
                placeholder="Search products ..."
                className="w-full bg-[#1e1e1e] border border-[#2a2a2a] text-white placeholder-gray-500 rounded-md py-2 pl-9 pr-2 text-sm focus:outline-none"
              />
              {isLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-3 h-3 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>

            {firstLetter ? (
              <Link
                to={dashboardLink}
                className="px-3 py-2 text-[13px] font-semibold text-orange-500 transition-colors duration-300 border border-orange-500 rounded-md whitespace-nowrap"
              >
                {vendor?.vendor?._id ? "Dashboard" : "Account"}
              </Link>
            ) : (
              <Link
                to="/selectpath"
                className="px-3 py-2 text-[13px] font-semibold text-orange-500 transition-colors duration-300 border border-orange-500 rounded-md whitespace-nowrap"
              >
                Get Started
              </Link>
            )}

            {/* Mobile wishlist/cart */}
            <div className="flex items-center gap-1">
              {/* <Link to="/dashboard/wishlist" className="relative p-1.5" aria-label="Wishlist">
                <BsHeartFill size={20} className="text-white" />
                {totalWishlistQuantity > 0 && (
                  <span className="absolute -top-1 -right-1 text-[10px] text-white bg-[#ff710b] rounded-full h-4 w-4 flex items-center justify-center font-bold">
                    {totalWishlistQuantity}
                  </span>
                )}
              </Link> */}
              <Link to="/cart" className="relative p-1.5" aria-label="Cart">
                <MdShoppingCart size={22} className="text-white" />
                {totalQuantity > 0 && (
                  <span className="absolute -top-1 -right-1 text-[10px] text-white bg-[#ff710b] rounded-full h-4 w-4 flex items-center justify-center font-bold">
                    {totalQuantity}
                  </span>
                )}
              </Link>
            </div>

            <button
              onClick={toggleMenu}
              className="text-white p-1"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
            >
              {menuOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
            </button>
          </div>

          {/* Search Results Mobile */}
          {items.length > 0 && word && (
            <div className="relative px-4 pb-4">
              <ul className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto max-h-60 text-black">
                {items.map((item) => (
                  <li key={item._id} className="border-b border-gray-100 last:border-b-0">
                    <Link
                      to={`/product/${item._id}`}
                      onClick={() => setWord("")}
                      className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors duration-150"
                    >
                      <span className="text-sm font-medium">{item.name}</span>
                      <span className="text-sm font-semibold text-orange-600">
                        {convertPrice(item.price, "USD", settings.currency).toLocaleString(
                          settings.language, { style: "currency", currency: settings.currency }
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* ── Mobile Sidebar Drawer ── */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={toggleMenu}
              className="fixed inset-0 bg-black/60 z-[60] md:hidden backdrop-blur-sm"
            />
            {/* Sidebar */}
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed top-0 left-0 bottom-0 w-[80%] max-w-[300px] bg-white z-[70] md:hidden shadow-2xl flex flex-col"
            >
              <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-black">
                <img src={Logo} alt="Mbaay" className="w-16 brightness-0 invert" />
                <button onClick={toggleMenu} className="text-white p-2">
                  <FaTimes size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto py-4">
                <nav className="flex flex-col space-y-1">
                  {firstLetter ? (
                    <Link
                      to={dashboardLink}
                      className="flex items-center gap-3 px-6 py-4 text-gray-800 hover:bg-orange-50 hover:text-orange-600 transition-colors font-semibold"
                      onClick={toggleMenu}
                    >
                      <FaUser size={18} className="text-orange-500" />
                      {vendor?.vendor?._id ? "Dashboard" : "Account"}
                    </Link>
                  ) : (
                    <Link
                      to="/selectpath"
                      className="flex items-center gap-3 px-6 py-4 text-gray-800 hover:bg-orange-50 hover:text-orange-600 transition-colors font-semibold"
                      onClick={toggleMenu}
                    >
                      <FaUser size={18} className="text-orange-500" />
                      Get Started
                    </Link>
                  )}
                  <Link
                    to="/dashboard/wishlist"
                    className="flex items-center gap-3 px-6 py-4 text-gray-600 hover:bg-orange-50 transition-colors"
                    onClick={toggleMenu}
                  >
                    <BsHeartFill size={18} />
                    Wishlist ({totalWishlistQuantity})
                  </Link>

                  <Link
                    to="/cart"
                    className="flex items-center gap-3 px-6 py-4 text-gray-600 hover:bg-orange-50 transition-colors"
                    onClick={toggleMenu}
                  >
                    <MdShoppingCart size={20} />
                    Cart ({totalQuantity})
                  </Link>

                  <div className="h-[1px] bg-gray-100 my-2 mx-6" />

                  {vendor?.vendor?._id && (
                    <Link
                      to="/app/all-post"
                      className="flex items-center gap-3 px-6 py-4 text-gray-600 hover:bg-orange-50 transition-colors"
                      onClick={toggleMenu}
                    >
                      Timeline
                    </Link>
                  )}

                  <Link
                    to="/about-us"
                    className="px-6 py-4 text-gray-600 hover:bg-orange-50 transition-colors"
                    onClick={toggleMenu}
                  >
                    About us
                  </Link>
                  <Link
                    to="/contact-us"
                    className="px-6 py-4 text-gray-600 hover:bg-orange-50 transition-colors"
                    onClick={toggleMenu}
                  >
                    Contact us
                  </Link>
                </nav>
              </div>

              {user && (
                <div className="p-4 border-t border-gray-100">
                  <button
                    className="flex items-center gap-3 px-6 py-4 text-red-600 hover:bg-red-50 w-full rounded-lg transition-colors font-medium text-left"
                    onClick={() => {
                      // Logout logic would go here
                      toggleMenu();
                    }}
                  >
                    <FaSignOutAlt size={18} />
                    Logout
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <Dropdown />
    </header>
  );
};

export default Header;